
import { supabase } from "@/integrations/supabase/client";

interface EarlyAccessResult {
  success: boolean;
  error?: string;
  message?: string;
}

// Test database connectivity
export const testSupabaseConnection = async (): Promise<boolean> => {
  try {
    console.log("🔍 Testing Supabase connection...");
    const { data, error } = await supabase
      .from('early_access_rebuild')
      .select('count', { count: 'exact', head: true });
    
    if (error) {
      console.error("❌ Connection test failed:", error);
      return false;
    }
    
    console.log("✅ Supabase connection successful");
    return true;
  } catch (error) {
    console.error("❌ Connection test error:", error);
    return false;
  }
};

// Retry function with exponential backoff
const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 Attempt ${attempt}/${maxRetries}`);
      return await operation();
    } catch (error: any) {
      lastError = error;
      console.error(`❌ Attempt ${attempt} failed:`, error);
      
      if (attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.log(`⏳ Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
};

export const registerForEarlyAccess = async (email: string): Promise<EarlyAccessResult> => {
  try {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return {
        success: false,
        error: 'invalid-email',
        message: 'Email format is invalid'
      };
    }

    // Normalize email
    const normalizedEmail = email.toLowerCase().trim();
    
    console.log("=== PROPMATCH EARLY ACCESS REGISTRATION ===");
    console.log("Processing email:", normalizedEmail);
    console.log("Timestamp:", new Date().toISOString());
    
    // Test connection first
    const isConnected = await testSupabaseConnection();
    if (!isConnected) {
      return {
        success: false,
        error: 'connection-error',
        message: 'No se puede conectar a la base de datos. Verifica tu conexión a internet.'
      };
    }
    
    // Check if email already exists with retry logic
    const checkEmailOperation = async () => {
      const { data: existingUser, error: checkError } = await supabase
        .from('early_access_rebuild')
        .select('email')
        .eq('email', normalizedEmail)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        // PGRST116 means no rows found, which is what we want
        throw checkError;
      }

      return existingUser;
    };

    console.log("Checking if email already exists...");
    const existingUser = await retryWithBackoff(checkEmailOperation);

    if (existingUser) {
      console.log('✅ Email already registered:', normalizedEmail);
      return {
        success: false,
        error: 'email-exists',
        message: 'Este email ya está registrado en nuestra lista VIP'
      };
    }
    
    // Attempt registration with retry logic
    console.log("Inserting new email into early_access_rebuild table...");
    
    const insertOperation = async () => {
  const { data, error: insertError } = await supabase
    .from('early_access_rebuild')
    .insert([{
      email: normalizedEmail,
      created_at: new Date().toISOString(),
      status: 'pending' // ⚠️ Solo si este campo existe en tu tabla
    }])
    .select();

      if (insertError) {
        throw insertError;
      }

      return data;
    };

    const data = await retryWithBackoff(insertOperation);

    console.log('🎉 SUCCESS: Email registered successfully');
    console.log('Registered email:', normalizedEmail);
    console.log('Registration data:', data);
    
    return {
      success: true,
      message: '¡Registro VIP completado! Recibirás tu token gratuito al lanzamiento.'
    };

  } catch (error: any) {
    console.error('=== SYSTEM ERROR ===');
    console.error('Unexpected error:', error);
    
    // Enhanced error categorization
    if (error?.message?.includes('fetch') || 
        error?.message?.includes('network') ||
        error?.name === 'NetworkError' ||
        error?.message?.includes('Failed to fetch') ||
        !navigator.onLine) {
      return {
        success: false,
        error: 'network-error',
        message: 'Error de conexión. Verifica tu internet e intenta nuevamente.'
      };
    }

    // Handle specific database errors
    if (error?.code === '23505' || 
        error?.message?.toLowerCase().includes('duplicate') || 
        error?.message?.toLowerCase().includes('already exists') ||
        error?.message?.toLowerCase().includes('unique constraint')) {
      console.log('✅ Email already registered (constraint violation):', email);
      return {
        success: false,
        error: 'email-exists',
        message: 'Este email ya está registrado en nuestra lista VIP'
      };
    }

    // CORS or authentication errors
    if (error?.message?.includes('CORS') || 
        error?.message?.includes('401') ||
        error?.message?.includes('403')) {
      return {
        success: false,
        error: 'auth-error',
        message: 'Error de autenticación. El equipo técnico ha sido notificado.'
      };
    }
    
    // Generic error fallback
    return {
      success: false,
      error: 'system-error',
      message: 'Error técnico temporal. Intenta nuevamente en unos momentos.'
    };
  }
};
